/* The practice log: every session of the trainer and every answer given in it, written to
   the account.

   The trainer never waits for the network, and runs without one. So a row goes to an outbox in
   the browser first, and from there to the database in the background. The database takes a
   row it already holds as done ("on conflict do nothing", on keys the client makes), so a
   flush that was cut off is simply run again. docs/specs/2026-09-20-practice-log-design.md */

import { actionSchema, cellSchema, z, type Action, type Cell } from "@preflop/core";
import { DataError } from "./collections";
import type { KV } from "./kv";

/** What the tables check, checked before a row is kept: the client is where a bad row is a
    bug to hear about, the database where it is refused. */
export const SEQ_MAX = 999;
export const MS_MAX = 3_600_000;
/** The table allows 4 KB of config; a little under, since jsonb is not its JSON text. */
const CONFIG_MAX_CHARS = 3500;

const timestamp = z.iso.datetime({ offset: true });

export const sessionRowSchema = z.object({
  id: z.uuid(),
  user_id: z.uuid(),
  collection_id: z.uuid().nullable(),
  config: z
    .record(z.string(), z.unknown())
    .refine((c) => JSON.stringify(c).length <= CONFIG_MAX_CHARS, "The setup is too large to log."),
  started_at: timestamp,
});
export type SessionRow = z.infer<typeof sessionRowSchema>;

export const answerRowSchema = z
  .object({
    session_id: z.uuid(),
    seq: z.number().int().min(0).max(SEQ_MAX),
    user_id: z.uuid(),
    spot_id: z.uuid().nullable(),
    spot_key: z.string().regex(/^[a-z0-9][a-z0-9.+-]{0,199}$/),
    hand: z.string().regex(/^[AKQJT2-9]{2}[so]?$/),
    cards: z
      .string()
      .regex(/^([AKQJT2-9][shdc]){2}$/)
      .nullable(),
    rnd: z.number().int().min(0).max(99),
    cell: cellSchema,
    chosen: actionSchema,
    accepted: z.array(actionSchema).max(4),
    correct: z.boolean(),
    points: z.number().min(0).max(1),
    ms: z.number().int().min(0).max(MS_MAX).nullable(),
    answered_at: timestamp,
  })
  .refine((a) => a.correct === a.accepted.includes(a.chosen), "correct must follow accepted")
  .refine((a) => (a.points === 1) === a.correct, "a full point is a right answer, and only that");
export type AnswerRow = z.infer<typeof answerRowSchema>;

/** Where the rows go. The Supabase client in the app, a stub in tests. Both inserts leave a row
    that is already there alone, and throw a `DataError` when the database refuses. */
export interface PracticeSource {
  /** Who the requests would be made as, or null when nobody is signed in. */
  userId(): Promise<string | null>;
  insertSessions(rows: SessionRow[]): Promise<void>;
  insertAnswers(rows: AnswerRow[]): Promise<void>;
}

export interface NewSession {
  userId: string;
  /** The collection drilled. */
  collectionId: string | null;
  /** The setup's filters, and `retryOf` on a retry. */
  config: Record<string, unknown>;
  startedAt?: Date;
}

export interface NewAnswer {
  spotId: string | null;
  spotKey: string;
  hand: string;
  /** As dealt, "AhKh". */
  cards: string | null;
  rnd: number;
  cell: Cell;
  chosen: Action;
  accepted: readonly Action[];
  points: number;
  /** From the hand being drawn to the answer. */
  ms: number | null;
  answeredAt?: Date;
}

export interface Recorder {
  /** The session's id: what a retry of its mistakes names as `retryOf`. */
  readonly sessionId: string;
  /** Keep one answer, and ask for it to be sent. Resolves once it is in the outbox, which is
      when it is safe; rejects only for a row that could never be stored. */
  answer(a: NewAnswer): Promise<void>;
}

export interface FlushResult {
  /** Rows the database took, sessions and answers together. */
  sent: number;
  /** Rows the database refused for good, and that were let go. */
  dropped: number;
  /** Answers of this user still waiting. */
  left: number;
}

export interface PracticeLog {
  /** A recorder for one run of the trainer. Nothing is written until its first answer: a
      session nobody answered leaves no trace. */
  open(session: NewSession): Recorder;
  /** Send what this user has waiting. Never rejects: what could not be sent is `left`. One
      flush runs at a time; one asked for meanwhile runs after it. */
  flush(userId: string): Promise<FlushResult>;
  /** How many of this user's answers wait in the outbox. */
  pending(userId: string): Promise<number>;
  /** Let go of what this user has waiting: the account is deleted, and nothing will take it. */
  forget(userId: string): Promise<void>;
}

export interface PracticeLogOptions {
  newId?: () => string;
  /** Called after every answer is safely in the outbox. The app flushes; a test may not. */
  onQueued?: (userId: string) => void;
}

const BATCH = 200;

/* Outbox keys carry the user, so that one account's rows can be found, counted and sent
   without reading another's; sorted, a session's answers are in the order they were given. */
const sessionKey = (r: SessionRow): string => `s:${r.user_id}:${r.id}`;
const answerKey = (r: AnswerRow): string =>
  `a:${r.user_id}:${r.session_id}:${String(r.seq).padStart(3, "0")}`;

/** A refusal that sending again will not change: bad data (22), a constraint (23), a policy
    or a grant (42). Anything else, no network or an expired token, is worth another try. */
const refused = (e: unknown): boolean => e instanceof DataError && /^(22|23|42)/.test(e.code);

interface Queued<R> {
  key: string;
  row: R;
}

export function createPracticeLog(
  source: PracticeSource,
  outbox: KV,
  { newId = () => crypto.randomUUID(), onQueued }: PracticeLogOptions = {},
): PracticeLog {
  async function queued<R>(prefix: string): Promise<Queued<R>[]> {
    const keys = (await outbox.keys()).filter((k) => k.startsWith(prefix)).sort();
    const out: Queued<R>[] = [];
    for (const key of keys) {
      const row = await outbox.get<R>(key);
      if (row) out.push({ key, row });
    }
    return out;
  }

  /**
   * Send these rows, in batches, and forget the ones that went. A refused batch is sent row
   * by row, so that one bad row does not hold back the rest; a row refused alone is tried
   * once more as `bare` makes it, then let go. A failure that is not a refusal is thrown with
   * the outbox as it stands.
   */
  async function send<R>(
    entries: Queued<R>[],
    insert: (rows: R[]) => Promise<void>,
    bare: (row: R) => R | null,
    tally: { sent: number; dropped: number },
  ): Promise<void> {
    const forget = async (done: Queued<R>[]): Promise<void> => {
      for (const { key } of done) await outbox.delete(key);
    };
    for (let at = 0; at < entries.length; at += BATCH) {
      const batch = entries.slice(at, at + BATCH);
      try {
        await insert(batch.map((e) => e.row));
        await forget(batch);
        tally.sent += batch.length;
        continue;
      } catch (e) {
        if (!refused(e)) throw e;
      }
      for (const entry of batch) {
        let took = false;
        for (const row of [entry.row, bare(entry.row)]) {
          if (!row) continue;
          try {
            await insert([row]);
            took = true;
            break;
          } catch (e) {
            if (!refused(e)) throw e;
          }
        }
        await forget([entry]);
        if (took) tally.sent++;
        else tally.dropped++;
      }
    }
  }

  const pending = async (userId: string): Promise<number> =>
    (await outbox.keys()).filter((k) => k.startsWith(`a:${userId}:`)).length;

  async function flushOnce(userId: string): Promise<FlushResult> {
    const tally = { sent: 0, dropped: 0 };
    try {
      // Signed out, or as someone else, every row would be refused, and a refusal drops it.
      if ((await source.userId()) === userId) {
        // Sessions first: an answer is only accepted into a session that exists. What may
        // have gone since the row was queued is the collection, or the spot; the key still
        // says which spot it was.
        await send<SessionRow>(
          await queued(`s:${userId}:`),
          (rows) => source.insertSessions(rows),
          (r) => (r.collection_id ? { ...r, collection_id: null } : null),
          tally,
        );
        await send<AnswerRow>(
          await queued(`a:${userId}:`),
          (rows) => source.insertAnswers(rows),
          (r) => (r.spot_id ? { ...r, spot_id: null } : null),
          tally,
        );
      }
    } catch {
      // No network, or a token that would not refresh. The outbox keeps what is left.
    }
    return { ...tally, left: await pending(userId).catch(() => 0) };
  }

  let running: Promise<FlushResult> | null = null;
  let next: Promise<FlushResult> | null = null;
  function flush(userId: string): Promise<FlushResult> {
    if (!running) {
      running = flushOnce(userId).finally(() => (running = null));
      return running;
    }
    // The one on its way may have read the outbox already: once more after it, however many ask.
    next ??= running.then(() => {
      next = null;
      return flush(userId);
    });
    return next;
  }

  return {
    open(session) {
      const row = sessionRowSchema.parse({
        id: newId(),
        user_id: session.userId,
        collection_id: session.collectionId,
        // Through JSON, so that what is kept is what a jsonb column can hold.
        config: JSON.parse(JSON.stringify(session.config)) as Record<string, unknown>,
        started_at: (session.startedAt ?? new Date()).toISOString(),
      } satisfies SessionRow);
      let seq = 0;
      let sessionQueued = false;
      // One write after the other: the session is in the outbox before its first answer.
      let tail: Promise<void> = Promise.resolve();

      return {
        sessionId: row.id,
        answer(a) {
          const at = seq++;
          const written = tail.then(async () => {
            const answer = answerRowSchema.parse({
              session_id: row.id,
              seq: at,
              user_id: row.user_id,
              spot_id: a.spotId,
              spot_key: a.spotKey,
              hand: a.hand,
              cards: a.cards,
              rnd: a.rnd,
              cell: a.cell,
              chosen: a.chosen,
              accepted: [...a.accepted],
              correct: a.accepted.includes(a.chosen),
              points: a.points,
              ms: a.ms === null ? null : Math.min(MS_MAX, Math.max(0, Math.round(a.ms))),
              answered_at: (a.answeredAt ?? new Date()).toISOString(),
            } satisfies AnswerRow);
            if (!sessionQueued) await outbox.set(sessionKey(row), row);
            sessionQueued = true;
            await outbox.set(answerKey(answer), answer);
            onQueued?.(row.user_id);
          });
          // A row that could not be kept does not stop the ones after it.
          tail = written.catch(() => undefined);
          return written;
        },
      };
    },
    flush,
    pending,
    async forget(userId) {
      for (const key of await outbox.keys())
        if (key.startsWith(`s:${userId}:`) || key.startsWith(`a:${userId}:`))
          await outbox.delete(key);
    },
  };
}
